import React, { useState, useEffect } from "react"
import moment from "moment"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import Button from "@mui/material/Button"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import Chip from "@mui/material/Chip"
import { isDarkColorByHex } from "utils/color"
import { PERMISSIONS } from "constants/permission"
import { SYSTEM_SETTINGS_INDEX } from "constants/global"
import { UsePermissionVerification } from "components/customHooks"
import MediaManager from "components/common/MediaManager"
import LoadingScreen from "components/common/LoadingScreen"
import { getAllSystemSettings } from "services/systemSetting"
import { verifyResponse } from "utils/httpRequest"
import { useSnackbar } from "notistack"
import SystemSettingDlg from "./SystemSettingDlg"
import { StyledSystemSetting, StyledSettingTable } from "./SystemSetting.style"

const getTypeMeta = (type) => {
  if (type === SYSTEM_SETTINGS_INDEX.HTML) return { label: "HTML", className: "html" }
  if (type === SYSTEM_SETTINGS_INDEX.NUMBER) return { label: "Number", className: "number" }
  if (type === SYSTEM_SETTINGS_INDEX.TEXT) return { label: "Text", className: "text" }
  if (type === SYSTEM_SETTINGS_INDEX.LINK) return { label: "Link", className: "link" }
  if (type === SYSTEM_SETTINGS_INDEX.COLOR) return { label: "Color", className: "color" }
  if (type === SYSTEM_SETTINGS_INDEX.IMAGE) return { label: "Image", className: "media" }
  if (type === SYSTEM_SETTINGS_INDEX.AUDIO) return { label: "Audio", className: "media" }
  if (type === SYSTEM_SETTINGS_INDEX.VIDEO) return { label: "Video", className: "media" }
  if (type === SYSTEM_SETTINGS_INDEX.ATTACHMENT) {
    return { label: "Attachment", className: "media" }
  }
  return { label: "Media", className: "media" }
}

const SystemSetting = () => {
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(false)
  const [openMediaManager, mediaManager] = MediaManager()
  const { enqueueSnackbar } = useSnackbar()

  const AccessControl = UsePermissionVerification()

  const [
    openSystemSettingDialog,
    systemSettingDialog,
    systemSettingDialogLoading,
  ] = SystemSettingDlg({
    onSubmit: () => {
      getSettings()
    },
    openMediaManager: openMediaManager,
  })

  const getSettings = async () => {
    try {
      setLoading(true)
      const response = await getAllSystemSettings()
      verifyResponse(response)
      setSettings(response.data || [])
    } catch {
      enqueueSnackbar("Failed to load system settings.", { variant: "warning" })
      setSettings([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getSettings()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listSystemSettingAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_SETTING.SYSTEM_SETTING_LIST_ALL,
      ]),
      editSystemSettingAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_SETTING.SYSTEM_SETTING_EDIT,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <StyledSystemSetting>
      <PageContent>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">System Settings</h1>
            <p className="page-subtitle">
              Manage global content settings, media references, and theme values.
            </p>
          </div>
        </PageHeading>
        <div className="page-surface">
          {AccessControl.Access?.listSystemSettingAccess && (
            <StyledSettingTable>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="system setting table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ width: 70 }}>
                        Tag
                      </TableCell>
                      <TableCell align="left" sx={{ width: 180 }}>
                        Setting Name
                      </TableCell>
                      <TableCell align="left">Description</TableCell>
                      <TableCell align="left" sx={{ minWidth: 340 }}>
                        Content
                      </TableCell>
                      <TableCell align="left" sx={{ width: 130 }}>
                        Modified
                      </TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.map((row) => {
                      const typeMeta = getTypeMeta(row.type)
                      const isColor = row.type === SYSTEM_SETTINGS_INDEX.COLOR
                      const isTextType =
                        row.type === SYSTEM_SETTINGS_INDEX.TEXT ||
                        row.type === SYSTEM_SETTINGS_INDEX.NUMBER ||
                        row.type === SYSTEM_SETTINGS_INDEX.LINK ||
                        row.type === SYSTEM_SETTINGS_INDEX.HTML
                      const contentText =
                        row.content || row.resourceDto?.name || row.resourceDto?.url || "-"

                      return (
                        <TableRow key={`system-setting-item-${row.id}`}>
                          <TableCell scope="row">{row.tag}</TableCell>
                          <TableCell scope="row">{row.title}</TableCell>
                          <TableCell scope="row">{row.description || "-"}</TableCell>
                          <TableCell align="left">
                            <Chip
                              className={`type-chip ${typeMeta.className}`}
                              label={typeMeta.label}
                              size="small"
                            />
                            {isColor ? (
                              <div
                                className={`color-box ${
                                  isDarkColorByHex(row.content) ? "dark-mode" : ""
                                }`}
                                style={{
                                  backgroundColor: row.content,
                                }}
                              >
                                {row.content}
                              </div>
                            ) : (
                              <div className="content-line">
                                {isTextType ? row.content : contentText}
                              </div>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            <div className="modified-date">
                              {moment(row.updatedAt).format("lll")}
                            </div>
                          </TableCell>
                          <TableCell align="right">
                            {AccessControl.Access.editSystemSettingAccess && (
                              <div className="row-actions">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    openSystemSettingDialog(row)
                                  }}
                                >
                                  Edit
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    {settings.length === 0 && (
                      <TableRow>
                        <TableCell className="table-empty" colSpan={6}>
                          No system settings found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledSettingTable>
          )}
          {!AccessControl.Access?.listSystemSettingAccess && (
            <div className="page-empty">
              You do not have permission to view system settings.
            </div>
          )}
        </div>

        {systemSettingDialog}
        {mediaManager}
        <LoadingScreen loading={loading || systemSettingDialogLoading} />
      </PageContent>
    </StyledSystemSetting>
  )
}

export default SystemSetting
